import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  FileText, Plus, Settings, LogOut, Package, 
  TrendingUp, Clock, CheckCircle, Send, Home, CreditCard, Shield, Menu, X
} from "lucide-react";
import { toast } from "sonner";
import UsageCard from "../components/UsageCard";
import QuotaExhaustedModal from "../components/QuotaExhaustedModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const Dashboard = ({ user, API }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quotaStatus, setQuotaStatus] = useState(null);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Admin whitelist
  const ADMIN_EMAILS = [
    "tobygeorgesmith@outlook.com",
    "doug@tradesoffice.co.uk",
    "toby@tradesoffice.co.uk"
  ];
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());

  useEffect(() => {
    fetchStats();
    fetchQuotaStatus();
    
    // Check for successful borrow/payg from URL params
    const borrowed = searchParams.get("borrowed");
    const payg = searchParams.get("payg");
    if (borrowed) {
      toast.success(`Successfully borrowed ${borrowed} quotes from next month!`);
      window.history.replaceState({}, '', '/dashboard');
    }
    if (payg) {
      toast.success(`Successfully purchased ${payg} PAYG credits!`);
      window.history.replaceState({}, '', '/dashboard');
    }
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/dashboard/stats`, {
        credentials: "include"
      });
      if (!response.ok) throw new Error("Failed to fetch stats");
      const data = await response.json();
      setStats(data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotaStatus = async () => {
    try {
      const response = await fetch(`${API}/quota/status`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setQuotaStatus(data);
      }
    } catch (error) {
      console.error("Failed to fetch quota status");
    }
  };

  const handleNewQuote = () => {
    // Check if quota is exhausted before allowing new quote
    if (quotaStatus && !quotaStatus.can_quote && !quotaStatus.is_unlimited) {
      setShowQuotaModal(true);
      return;
    }
    navigate("/quote/new");
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include"
      });
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/", { replace: true });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-secondary text-secondary-foreground",
      sent: "bg-blue-500/20 text-blue-400",
      accepted: "bg-green-500/20 text-green-400",
      rejected: "bg-destructive/20 text-destructive"
    };
    return styles[status] || styles.draft;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-bold">{user?.company_name || "Trade Office"}</h1>
                <p className="text-sm text-muted-foreground hidden sm:block">{user?.email}</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-2">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon" data-testid="nav-home-btn">
                  <Home className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/subscription">
                <Button variant="ghost" size="icon" data-testid="nav-pricing-btn">
                  <CreditCard className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/materials">
                <Button variant="ghost" size="icon" data-testid="nav-materials-btn">
                  <Package className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/settings">
                <Button variant="ghost" size="icon" data-testid="nav-settings-btn">
                  <Settings className="w-5 h-5" />
                </Button>
              </Link>
              {isAdmin && (
                <Link to="/admin">
                  <Button variant="ghost" size="icon" data-testid="nav-admin-btn" title="Admin Dashboard">
                    <Shield className="w-5 h-5 text-primary" />
                  </Button>
                </Link>
              )}
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </nav>

            {/* Mobile Navigation - Dropdown Menu */}
            <div className="md:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                    <Menu className="w-6 h-6" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="mobile-nav-home">
                    <Home className="w-4 h-4 mr-3" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/subscription')} data-testid="mobile-nav-pricing">
                    <CreditCard className="w-4 h-4 mr-3" />
                    Subscription
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/materials')} data-testid="mobile-nav-materials">
                    <Package className="w-4 h-4 mr-3" />
                    Materials
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="mobile-nav-settings">
                    <Settings className="w-4 h-4 mr-3" />
                    Settings
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="mobile-nav-admin">
                        <Shield className="w-4 h-4 mr-3 text-primary" />
                        Admin Dashboard
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="mobile-logout-btn" className="text-destructive focus:text-destructive">
                    <LogOut className="w-4 h-4 mr-3" />
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Top Section: CTA + Usage Card */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* New Quote CTA */}
          <div className="lg:col-span-2">
            <Button
              data-testid="new-quote-btn"
              onClick={handleNewQuote}
              size="lg"
              className="h-16 px-8 text-lg font-bold uppercase tracking-wide bg-primary text-primary-foreground hover:bg-primary/90 btn-glow w-full"
            >
              <Plus className="w-6 h-6 mr-3" />
              Create New Quote
            </Button>
          </div>
          
          {/* Usage Card */}
          <div className="lg:col-span-1">
            <UsageCard API={API} />
          </div>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map(i => (
              <Card key={i} className="bg-card border-white/5">
                <CardContent className="p-6">
                  <div className="h-16 bg-secondary/50 animate-pulse rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card 
              className="bg-card border-white/5 cursor-pointer hover:border-primary/50 transition-colors" 
              data-testid="stat-total-quotes"
              onClick={() => navigate("/quotes")}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Quotes</p>
                    <p className="text-3xl font-bold mono">{stats?.total_quotes || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className="bg-card border-white/5 cursor-pointer hover:border-primary/50 transition-colors" 
              data-testid="stat-draft-quotes"
              onClick={() => navigate("/quotes?status=draft")}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Drafts</p>
                    <p className="text-3xl font-bold mono">{stats?.draft_quotes || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className="bg-card border-white/5 cursor-pointer hover:border-primary/50 transition-colors" 
              data-testid="stat-sent-quotes"
              onClick={() => navigate("/quotes?status=sent")}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center">
                    <Send className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Sent</p>
                    <p className="text-3xl font-bold mono">{stats?.sent_quotes || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className="bg-card border-white/5 cursor-pointer hover:border-primary/50 transition-colors" 
              data-testid="stat-revenue"
              onClick={() => navigate("/quotes?status=accepted")}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Revenue</p>
                    <p className="text-2xl font-bold mono">{formatCurrency(stats?.total_revenue)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recent Quotes */}
        <Card className="bg-card border-white/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Recent Quotes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-20 bg-secondary/50 animate-pulse rounded" />
                ))}
              </div>
            ) : stats?.recent_quotes?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_quotes.map((quote) => (
                  <div
                    key={quote.quote_id}
                    data-testid={`quote-item-${quote.quote_id}`}
                    className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/quote/${quote.quote_id}`)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold">{quote.customer_name}</h3>
                        <Badge className={getStatusBadge(quote.status)}>
                          {quote.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {quote.job_description}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="font-bold mono text-lg">{formatCurrency(quote.total)}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(quote.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No quotes yet</p>
                <Button
                  onClick={handleNewQuote}
                  className="bg-primary text-primary-foreground"
                  data-testid="empty-new-quote-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Quote
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Quota Exhausted Modal */}
      <QuotaExhaustedModal
        isOpen={showQuotaModal}
        onClose={() => setShowQuotaModal(false)}
        quotaStatus={quotaStatus}
        API={API}
        onQuotaUpdated={() => {
          fetchQuotaStatus();
          setShowQuotaModal(false);
        }}
      />
    </div>
  );
};

export default Dashboard;
